import { Box, Text } from '@factorialco/deprecated-design-system'
import { OneDataCollection } from '@factorialco/f0-react/dist/experimental'
import { Download } from '@factorialco/f0-react/icons/app'
import {
  IntegrationsSyncRunOutputFragment,
  SyncRunOutputsDocument,
  SyncRunOutputsParametersInput,
} from 'generated/resources/graphql'

import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import { i18nDateToString, stringToDate } from 'modules/core/lib/dates'
import { download } from 'modules/core/lib/download'
import i18n from 'modules/core/lib/i18n'

import { usePrivateIntegrationsSyncContext } from 'modules/integrations/hooks/SyncContext'

export interface EmptyState {
  title: string
  description: string
}

interface Props {
  filterParameters: SyncRunOutputsParametersInput[]
  customEmptyState?: EmptyState
}

const ExportedDocumentsCollection = ({ filterParameters, customEmptyState }: Props) => {
  const { syncEnabled, capabilityMode, marketplaceIntegration, isBookkeeperView } =
    usePrivateIntegrationsSyncContext()

  const bookkeeperContext = {
    isBookkeeperView,
  }

  const emptyState = {
    'no-data': {
      title: customEmptyState?.title ?? i18n.t('integrations.exported_documents.empty_state.title'),
      description:
        customEmptyState?.description ??
        i18n.t('integrations.exported_documents.empty_state.description'),
    },
  }

  const sourceFiles = useGraphqlDataCollectionSource({
    document: SyncRunOutputsDocument,
    queryContext: bookkeeperContext,
    mapData: (data) => data?.integrations?.syncRunOutputsConnection.nodes ?? [],
    mapPagination: (data) => ({
      total: data.integrations?.syncRunOutputsConnection.totalCount ?? 0,
    }),
    paginationType: 'infinite-scroll',
    perPage: 10,
    mapVariables: () => {
      return {
        parameters: filterParameters,
        sortOrder: {
          field: 'id',
          order: 'desc',
        },
      }
    },
    defaultSortings: {
      field: 'id',
      order: 'desc',
    },
    filters: {},
    presets: [],
    itemActions: (file: IntegrationsSyncRunOutputFragment) => [
      {
        label: i18n.t('common.download'),
        icon: Download,
        onClick: () => download(file.fileUrl, file.fileName),
        type: 'primary',
      },
    ],
  })

  if (!syncEnabled || capabilityMode !== 'file') return null

  return (
    <Box padding="s24">
      <Text.Normal color="grey1000" weight="semibold">
        {i18n.t('integrations.exported_documents.title', {
          integrations: marketplaceIntegration?.displayName,
        })}
      </Text.Normal>
      <Box alignItems="center" paddingX="s24">
        <OneDataCollection
          storage={false}
          source={sourceFiles}
          emptyStates={emptyState}
          visualizations={[
            {
              type: 'list',
              options: {
                itemDefinition: (item: IntegrationsSyncRunOutputFragment) => {
                  const creationDate = i18nDateToString(
                    stringToDate(item.createdAt, 'backend_datetime'),
                    'ui_short_with_time'
                  )
                  return {
                    title: item.fileName,
                    description: [creationDate],
                    avatar: {
                      type: 'file',
                      file: {
                        name: item.fileName,
                        type: '',
                      },
                    },
                  }
                },
                fields: [],
              },
            },
          ]}
        />
      </Box>
    </Box>
  )
}

export default ExportedDocumentsCollection
